// Small shared building blocks for the admin + console screens added with
// migration 20261022000008 (family licences, billing, children, analytics, the
// owner console). The older admin screens each carry their own copies of these;
// new screens share one set so they look and behave the same.
//
// Rules every piece keeps: logical CSS (insetInline / paddingInline / textAlign
// "start") so a right-to-left locale works, tap targets of at least 44px, and
// meaning carried by WORDS — a chip always has a label, never a colour alone.

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
} from "react";
import type { Locale } from "../../i18n/dictionary";
import { localeTag } from "../../progress/text";
import { PortalWriteError } from "../../data/supabaseAdapter";
import { shouldUseSupabase } from "../../data";
import { getDemoVariant, subscribeDemoVariant, type DemoVariant } from "../../data/demoVariant";

// ---- Formatting --------------------------------------------------------------

export function formatMoney(locale: Locale, amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(localeTag(locale), {
      style: "currency",
      currency: currency || "MYR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

export function formatNumber(locale: Locale, n: number, digits = 2): string {
  return new Intl.NumberFormat(localeTag(locale), { maximumFractionDigits: digits }).format(n);
}

/** "YYYY-MM-DD" or an ISO instant → "6 Oct 2026". An unreadable value → "—". */
export function formatDate(locale: Locale, value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(localeTag(locale), {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** "YYYY-MM" → "Oct 2026". */
export function formatMonth(locale: Locale, ym: string, withYear = true): string {
  const d = new Date(`${ym}-01T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return ym;
  return d.toLocaleDateString(localeTag(locale), {
    month: "short",
    ...(withYear ? { year: "numeric" } : {}),
    timeZone: "UTC",
  });
}

/** Today as "YYYY-MM-DD" (UTC), for date-input `min`. */
export function todayIso(nowMs: number = Date.now()): string {
  return new Date(nowMs).toISOString().slice(0, 10);
}

/** End of the chosen day, UTC — what a "ends on" date input means. */
export function endOfDay(yyyyMmDd: string): string {
  return `${yyyyMmDd}T23:59:59.999Z`;
}

export function looksLikeEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

/** The SQLSTATE a write failed with, when it came from Postgres. */
export function errorCode(err: unknown): string | undefined {
  return err instanceof PortalWriteError ? err.code : undefined;
}

// ---- Demo variant ------------------------------------------------------------

/** The demo organisation being shown (demo mode only; "centre" when live). */
export function useDemoVariant(): DemoVariant {
  const v = useSyncExternalStore(subscribeDemoVariant, getDemoVariant, getDemoVariant);
  return shouldUseSupabase() ? "centre" : v;
}

// ---- Loading ------------------------------------------------------------------

export interface Loaded<T> {
  data: T | null;
  loading: boolean;
  error: unknown;
  reload: () => void;
  /** Re-reads without flipping back to the loading state (after a write). */
  refresh: () => Promise<void>;
}

/**
 * Runs `fn` on mount and whenever `deps` change. A failure is kept as `error`
 * and never collapses into "empty"; a refresh after a write keeps the screen
 * on screen while it reads.
 */
export function useLoad<T>(fn: () => Promise<T>, deps: readonly unknown[]): Loaded<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [nonce, setNonce] = useState(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Through a resolved promise so a synchronous throw becomes an error state.
    Promise.resolve()
      .then(() => fnRef.current())
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setData(null);
          setError(e ?? new Error("failed"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const refresh = useCallback(async () => {
    try {
      setData(await fnRef.current());
      setError(null);
    } catch {
      // A failed silent refresh leaves what is on screen; the write itself succeeded.
    }
  }, []);

  return { data, loading, error, reload, refresh };
}

// ---- Layout -------------------------------------------------------------------

export function PageHeader({ title, subtitle, children }: { title: string; subtitle?: string; children?: ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
      <div style={{ minWidth: 0, flex: "1 1 320px" }}>
        <h1 style={{ fontSize: 30, color: "var(--ink-deep)", letterSpacing: "-.02em", margin: 0, lineHeight: 1.2 }}>{title}</h1>
        {subtitle && (
          <p style={{ color: "var(--ink-soft)", fontSize: 16.5, lineHeight: 1.55, margin: "8px 0 0", maxWidth: "70ch" }}>{subtitle}</p>
        )}
      </div>
      {children}
    </div>
  );
}

export const CARD: CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--line)",
  borderRadius: "var(--r-lg)",
  boxShadow: "var(--sh-1)",
  padding: 20,
  minWidth: 0,
};

export function Card({ title, children, style, id }: { title?: string; children: ReactNode; style?: CSSProperties; id?: string }) {
  return (
    <section style={{ ...CARD, ...style }} aria-labelledby={title && id ? id : undefined}>
      {title && (
        <h2 id={id} style={{ fontSize: 16.5, fontWeight: 700, color: "var(--ink-deep)", margin: "0 0 14px" }}>
          {title}
        </h2>
      )}
      {children}
    </section>
  );
}

export function StatTile({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) {
  return (
    <div style={{ ...CARD, padding: "16px 18px", display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-soft)" }}>{label}</span>
      <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 26, color: "var(--ink-deep)", lineHeight: 1.15, overflowWrap: "anywhere" }}>
        {value}
      </span>
      {hint && <span style={{ fontSize: 13, color: "var(--ink-faint)", lineHeight: 1.45 }}>{hint}</span>}
    </div>
  );
}

export const GRID_TILES: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 170px), 1fr))",
  gap: 12,
};

// ---- Chips, notices -------------------------------------------------------------

export type Tone = "good" | "attention" | "neutral" | "muted" | "info";

const TONE: Record<Tone, { bg: string; fg: string; dot: string }> = {
  good: { bg: "var(--sage-tint)", fg: "var(--sage-deep)", dot: "var(--good)" },
  attention: { bg: "var(--peach-tint)", fg: "var(--peach-deep)", dot: "var(--peach-deep)" },
  neutral: { bg: "var(--surface-sunk)", fg: "var(--ink-soft)", dot: "var(--ink-soft)" },
  muted: { bg: "var(--surface-sunk)", fg: "var(--ink-faint)", dot: "var(--ink-faint)" },
  info: { bg: "var(--lilac-tint)", fg: "var(--lilac-deep)", dot: "var(--lilac-deep)" },
};

export function Chip({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) {
  const t = TONE[tone];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 10px",
        borderRadius: "var(--r-pill)",
        background: t.bg,
        color: t.fg,
        fontSize: 12.5,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: "50%", background: t.dot, flex: "none" }} />
      {children}
    </span>
  );
}

export function Notice({ tone = "info", title, children, live }: { tone?: Tone; title?: string; children?: ReactNode; live?: boolean }) {
  const t = TONE[tone];
  return (
    <div
      role={live ? "status" : undefined}
      aria-live={live ? "polite" : undefined}
      style={{
        background: t.bg,
        border: "1px solid var(--line)",
        borderRadius: "var(--r-md)",
        padding: "12px 16px",
        color: "var(--ink)",
        fontSize: 14.5,
        lineHeight: 1.55,
      }}
    >
      {title && <b style={{ display: "block", color: t.fg, fontSize: 15, marginBottom: children ? 4 : 0 }}>{title}</b>}
      {children}
    </div>
  );
}

export function ErrorPanel({ title, message, onRetry, retryLabel }: { title: string; message: string; onRetry?: () => void; retryLabel: string }) {
  return (
    <div
      role="alert"
      style={{
        background: "var(--peach-tint)",
        border: "1px solid var(--peach-deep)",
        borderRadius: "var(--r-lg)",
        padding: "20px 22px",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 8,
      }}
    >
      <b style={{ fontSize: 15.5, color: "var(--peach-deep)" }}>{title}</b>
      <span style={{ fontSize: 14.5, color: "var(--ink)", lineHeight: 1.55 }}>{message}</span>
      {onRetry && (
        <Button variant="secondary" onClick={onRetry}>
          {retryLabel}
        </Button>
      )}
    </div>
  );
}

export function LoadingLine({ label }: { label: string }) {
  return (
    <p role="status" aria-live="polite" style={{ color: "var(--ink-faint)", fontSize: 14, margin: "14px 0" }}>
      {label}
    </p>
  );
}

// ---- Controls -------------------------------------------------------------------

type ButtonVariant = "primary" | "secondary" | "quiet";

export function Button({ variant = "primary", style, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }) {
  const base: CSSProperties = {
    minHeight: 44,
    padding: "0 18px",
    borderRadius: "var(--r-pill)",
    fontFamily: "var(--font-body)",
    fontWeight: 600,
    fontSize: 14.5,
    cursor: rest.disabled ? "not-allowed" : "pointer",
    opacity: rest.disabled ? 0.55 : 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    whiteSpace: "nowrap",
  };
  const look: Record<ButtonVariant, CSSProperties> = {
    primary: { border: 0, background: "var(--brand)", color: "#fff" },
    secondary: { border: "1.5px solid var(--line-strong)", background: "var(--surface)", color: "var(--ink)" },
    quiet: { border: 0, background: "transparent", color: "var(--brand)", padding: "0 10px", textDecoration: "underline", textUnderlineOffset: 3 },
  };
  return <button type="button" {...rest} style={{ ...base, ...look[variant], ...style }} />;
}

export const INPUT: CSSProperties = {
  minHeight: 46,
  padding: "10px 14px",
  fontSize: 15.5,
  fontFamily: "var(--font-body)",
  color: "var(--ink)",
  background: "var(--surface)",
  border: "2px solid var(--line-strong)",
  borderRadius: "var(--r-md)",
  width: "100%",
  minWidth: 0,
};

export function Field({
  label,
  hint,
  error,
  style,
  ...input
}: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string; error?: string }) {
  const auto = useId();
  const id = input.id ?? auto;
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5, minWidth: 0, ...style }}>
      <label htmlFor={id} style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-soft)" }}>
        {label}
      </label>
      <input
        {...input}
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={[hintId, errId].filter(Boolean).join(" ") || undefined}
        style={INPUT}
      />
      {hint && (
        <span id={hintId} style={{ fontSize: 13, color: "var(--ink-faint)", lineHeight: 1.45 }}>
          {hint}
        </span>
      )}
      {error && (
        <span id={errId} style={{ fontSize: 13.5, color: "var(--peach-deep)", fontWeight: 600 }}>
          {error}
        </span>
      )}
    </div>
  );
}

// ---- Tables ---------------------------------------------------------------------

/** Text for screen readers only (e.g. an actions column header). */
export const SR_ONLY: CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

export const TH: CSSProperties = {
  padding: "10px 14px",
  textAlign: "start",
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: ".08em",
  textTransform: "uppercase",
  color: "var(--ink-faint)",
  borderBottom: "1px solid var(--line)",
  whiteSpace: "nowrap",
};

export const TD: CSSProperties = {
  padding: "13px 14px",
  fontSize: 14.5,
  color: "var(--ink)",
  borderBottom: "1px solid var(--line)",
  verticalAlign: "middle",
};

export function TableCard({ label, children, minWidth = 560 }: { label: string; children: ReactNode; minWidth?: number }) {
  return (
    <div style={{ ...CARD, padding: 0, overflow: "hidden" }}>
      {/* position: relative keeps an absolutely positioned SR_ONLY header inside
          the scroller instead of widening the whole page. */}
      <div style={{ overflowX: "auto", position: "relative" }}>
        <table aria-label={label} style={{ width: "100%", borderCollapse: "collapse", minWidth }}>
          {children}
        </table>
      </div>
    </div>
  );
}

// ---- Confirm dialog ---------------------------------------------------------------

export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  cancelLabel,
  busy,
  error,
  onConfirm,
  onCancel,
  children,
}: {
  title: string;
  body: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  busy?: boolean;
  error?: string;
  onConfirm: () => void;
  onCancel: () => void;
  children?: ReactNode;
}) {
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>("button")?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onCancel]);
  return (
    <>
      <div onClick={busy ? undefined : onCancel} style={{ position: "fixed", inset: 0, background: "rgba(35,44,56,.4)", zIndex: 80 }} />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{
          position: "fixed",
          zIndex: 81,
          insetInline: 16,
          top: "50%",
          transform: "translateY(-50%)",
          margin: "0 auto",
          maxWidth: 460,
          maxHeight: "calc(100vh - 32px)",
          overflowY: "auto",
          background: "var(--surface)",
          borderRadius: "var(--r-xl)",
          boxShadow: "var(--sh-2)",
          padding: "24px 22px",
        }}
      >
        <h2 id={titleId} style={{ fontSize: 20, color: "var(--ink-deep)", margin: 0 }}>
          {title}
        </h2>
        <div style={{ color: "var(--ink-soft)", fontSize: 15, lineHeight: 1.6, marginTop: 10 }}>{body}</div>
        {children}
        {error && (
          <p role="alert" style={{ marginTop: 12, color: "var(--peach-deep)", fontSize: 14, fontWeight: 600 }}>
            {error}
          </p>
        )}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap", marginTop: 20 }}>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button onClick={onConfirm} disabled={busy}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </>
  );
}

// ---- Tabs -------------------------------------------------------------------------

export function Tabs<K extends string>({
  label,
  tabs,
  value,
  onChange,
}: {
  label: string;
  tabs: { key: K; label: string }[];
  value: K;
  onChange: (k: K) => void;
}) {
  return (
    <div role="tablist" aria-label={label} style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={value === t.key}
          onClick={() => onChange(t.key)}
          className="zseg"
          data-on={String(value === t.key)}
          style={{
            minHeight: 44,
            padding: "0 16px",
            border: "1px solid var(--line-strong)",
            borderRadius: "var(--r-pill)",
            background: value === t.key ? "var(--brand-tint)" : "var(--surface)",
            color: value === t.key ? "var(--brand-hover)" : "var(--ink-soft)",
            fontFamily: "var(--font-body)",
            fontWeight: value === t.key ? 700 : 600,
            fontSize: 14.5,
            cursor: "pointer",
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}
